import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiNotification3Line } from '@remixicon/react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from './ui/DropdownMenu'
import type { Notification } from 'shared'
import { useNotifications } from '../hooks/useNotifications'
import { formatRelativeTime } from '../lib/dates'
import { cx, focusRing } from '../lib/utils'

export default function NotificationBell() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications()

  const handleOpen = (notification: Notification) => {
    if (!notification.read_at) markRead(notification.id)
    if (notification.link) navigate(notification.link)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('notifications.title')}
          className={cx(
            'relative flex size-9 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-50',
            focusRing,
          )}
        >
          <RiNotification3Line className="size-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="absolute right-1.5 top-1.5 flex min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold leading-4 text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-gray-800">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('notifications.title')}
          </span>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className={cx(
                'rounded px-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400',
                focusRing,
              )}
            >
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('notifications.empty')}
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto py-1">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => handleOpen(notification)}
                  className={cx(
                    'flex w-full flex-col gap-0.5 px-4 py-2.5 text-left transition hover:bg-gray-50 dark:hover:bg-gray-900',
                    focusRing,
                  )}
                >
                  <span className="flex items-center gap-2">
                    {!notification.read_at && (
                      <span className="size-1.5 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
                    )}
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                      {notification.title}
                    </span>
                  </span>
                  {notification.body && (
                    <span className="line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                      {notification.body}
                    </span>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {formatRelativeTime(notification.created_at, i18n.language)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
